import pluralize from 'pluralize';

import { capitalize } from '../../../../../../../../../util/text';
import { getPrintPackageConfigurableItems } from '../../../../../../../../../util/print-packages';
import { getPrintServiceLabels } from '../../../../../../../../../util/print-product-configuration';
import { isStandardPrint } from '../../../../../../../../../util/print-product';

/**
 * Returns the name of a PrintPackageItem when it has a PrintProductConfiguration.
 *
 * @param {Object} printPackageItem The PrintPackageItem.
 * @returns {string}
 */
export const getPrintPackageItemPrintProductName = printPackageItem => {
  if (!printPackageItem?.print_product_configuration) {
    return '';
  }

  const printProduct =
    printPackageItem.print_product_configuration.print_product;
  const printProductName = printProduct.name;
  const printProductSizeName =
    printPackageItem.print_product_configuration.print_product_size.print_size
      .name;

  // Only remove the "Standard Prints" name if it actually says "Standard".
  if (
    isStandardPrint(printProduct) &&
    (printProductName ?? '').toLowerCase().includes('standard')
  ) {
    return printProductSizeName;
  }

  return `${pluralize(printProductName, 1)} - ${printProductSizeName}`;
};

/**
 * Returns the quantity and display name of a print package item.
 *
 * @param {Object} printPackageItem The PrintPackageItem.
 * @param {Boolean} ignoreQuantity whether or not to ignore the quantity label
 *
 * @returns {string}
 */
export const getPrintPackageItemListingLabel = (
  printPackageItem,
  ignoreQuantity = false,
) => {
  const name = (() => {
    if (printPackageItem?.grants_purchased_identity) {
      return 'Digital Photo Pass';
    }

    if (printPackageItem?.digital_bundle) {
      return `Digital Photo Bundle: ${pluralize(
        'Photo',
        printPackageItem?.digital_bundle?.photos_count ?? 1,
        true,
      )}`;
    }

    return (
      printPackageItem.type?.name ??
      printPackageItem.custom_print?.name ??
      getPrintPackageItemPrintProductName(printPackageItem)
    );
  })();

  const { quantity } = printPackageItem;

  const itemServiceLabels = getPrintServiceLabels(
    printPackageItem.print_product_configuration,
  );

  const itemServiceLabelsStr =
    itemServiceLabels.length > 0
      ? ` – Includes ${itemServiceLabels.join(', ')}.`
      : '';

  // Custom prints keep their given name; the wallet rename only applies to
  // auto-generated names like "4 wallets".
  if (
    name.toLowerCase().includes('wallet') &&
    !printPackageItem.print_product_configuration &&
    !printPackageItem.custom_print
  ) {
    const [walletsQuantity, walletsName] = name?.split(' ') ?? ['', ''];
    const trueQuantity = quantity * walletsQuantity;
    const quantityLabel = ignoreQuantity ? '' : `(${trueQuantity}) `;

    return `${quantityLabel}${capitalize(walletsName)}${itemServiceLabelsStr}`;
  }

  const quantityLabel = ignoreQuantity ? '' : `(${quantity}) `;

  // Custom prints are named by the PRO, so they are shown verbatim.
  if (name && name === printPackageItem.custom_print?.name) {
    return `${quantityLabel}${name}${itemServiceLabelsStr}`;
  }

  // Do not pluralize if the label is something like 8x10 or 4x6 etc.
  if (name?.split('x')?.length > 1) {
    return `${quantityLabel}${name}${itemServiceLabelsStr}`;
  }

  return `${quantityLabel}${pluralize(
    name,
    ignoreQuantity ? 1 : quantity,
  )}${itemServiceLabelsStr}`;
};

export const getPrintPackageCartItemNonConfiguredItems = (
  cartItem,
  allowMultiPose = false,
) => {
  if (!cartItem) {
    return [];
  }

  const {
    albumPrintPackage,
    configurableItems = [],
    selectedPrintPackageItems = [],
  } = cartItem;
  const { print_package: printPackage } = albumPrintPackage ?? {};

  if (!printPackage) {
    return [];
  }

  if (printPackage?.max_checkout_items) {
    return getPrintPackageConfigurableItems(
      albumPrintPackage,
      allowMultiPose,
    ).filter(
      printPackageItem =>
        selectedPrintPackageItems.find(
          item => item.id === printPackageItem.id,
        ) &&
        !configurableItems.find(
          configurableCartItem =>
            configurableCartItem.printPackageItem?.id === printPackageItem?.id,
        ),
    );
  }

  return getPrintPackageConfigurableItems(
    albumPrintPackage,
    allowMultiPose,
  ).filter(
    printPackageItem =>
      !configurableItems.find(
        configurableCartItem =>
          configurableCartItem.printPackageItem?.id === printPackageItem?.id,
      ),
  );
};
